/* eslint-disable max-lines */
import { useState, useEffect, useCallback, useRef } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import { IPopUpWidgetProps } from "@swiggy-private/dls-react-mxn/dist/WidgetController/widgets/PopUpWidget/types";
import { IFrequencyCappingConfig } from "@swiggy-private/dls-react-mxn/dist/WidgetController/widgets/PopUpWidget/types";
import { IApiConfigs } from "@swiggy-private/portal-dineout-context";
import { getPopupCards } from "../utils";
import {
    checkPopupFrequencyCapping,
    updatePopupFrequencyCappingWithConfig,
    POPUP_WIDGET_SHOWN_STORAGE_KEY,
} from "../utils/frequencyCapping";

interface UsePopupCardsOptions {
    configs?: IApiConfigs;
    tableId: string;
    onWidgetView?: (widgetData?: Record<string, unknown>) => void;
    cards: IPLWidgetControllerCards[];
}

interface UsePopupCardsReturn {
    popupCards: IPLWidgetControllerCards[];
    handleWidgetView: (widgetData?: Record<string, unknown>) => void;
}

/**
 * Generic hook to extract and manage popup cards from API configs with frequency capping.
 * Works with any page that receives POPUP configs in the API response.
 */
export const usePopupCards = ({
    cards,
    configs,
    tableId,
    onWidgetView,
}: UsePopupCardsOptions): UsePopupCardsReturn => {
    const [popupCards, setPopupCards] = useState<IPLWidgetControllerCards[]>([]);
    const initailRenderRef = useRef<boolean>(false);
    // Extract and filter popup cards based on frequency capping
    const filterPopupsWithFrequencyCapping = useCallback(async () => {
        if (!configs || !tableId || isEmpty(cards)) {
            setPopupCards([]);
            return;
        }

        const allPopups = getPopupCards(configs);
        const validPopups: IPLWidgetControllerCards[] = [];

        for (const popup of allPopups) {
            const popupCard = popup.card?.card as IPopUpWidgetProps;
            const entityId = popupCard?.entityId;

            const frequencyCapping = popupCard?.frequencyCapping as IFrequencyCappingConfig;
            const isFrequencyCappingEnabled = frequencyCapping?.cappingEnabled ?? true;
            const maxCount = popupCard?.maxCount ?? 1;

            // Check frequency capping using internal functions
            const isCapped = await checkPopupFrequencyCapping({
                key: POPUP_WIDGET_SHOWN_STORAGE_KEY,
                tableId,
                entityId: entityId || "",
                frequencyCappingConfig: {
                    isFrequencyCappingEnabled,
                    maxCount,
                },
            });

            // If not capped which means we can still show the popup, include it in the list of valid popups
            if (!isCapped) {
                validPopups.push(popup);
            }
        }

        setPopupCards(validPopups);
    }, [cards, configs, tableId]);

    useEffect(() => {
        if (initailRenderRef.current) {
            return;
        }
        filterPopupsWithFrequencyCapping();
    }, [filterPopupsWithFrequencyCapping]);

    // Handle widget view callback
    // Updates frequency capping storage when popup is viewed
    const handleWidgetView = useCallback(
        async (widgetData?: Record<string, unknown>) => {
            if (initailRenderRef.current) {
                return;
            }
            // Call the provided callback (for analytics or other side effects)
            onWidgetView?.(widgetData);
            const entityId = widgetData?.entityId as string | undefined;

            const frequencyCapping = widgetData?.frequencyCapping as IFrequencyCappingConfig;
            const isFrequencyCappingEnabled = frequencyCapping?.cappingEnabled ?? true;
            const maxCount = Number(widgetData?.maxCount ?? 1);

            if (entityId && isFrequencyCappingEnabled) {
                await updatePopupFrequencyCappingWithConfig({
                    key: POPUP_WIDGET_SHOWN_STORAGE_KEY,
                    tableId,
                    entityId,
                    maxCount,
                });
            }
            initailRenderRef.current = true;
        },
        [tableId, onWidgetView]
    );

    return {
        popupCards,
        handleWidgetView,
    };
};
